import React from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { AutoComplete as AntDAutoComplete } from 'antd';
import { Tooltip } from 'components/common';
import styled from 'styled-components';

/** Matches IconLeftContainer width; text must start after this + a small gap. */
const ICON_LEFT_SLOT_PX = 40;
const ICON_LEFT_OFFSET_PX = 2;
const TEXT_INSET_LEFT_PX = 38;

const IconLeftContainer = styled.div`
  position: absolute;
  left: ${ICON_LEFT_OFFSET_PX}px;
  top: 0;
  bottom: 0;
  width: ${ICON_LEFT_SLOT_PX}px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
`;

const StyledAutoComplete = styled(AntDAutoComplete)`
  width: 100%;

  /*
   * Ant Design single Select keeps .ant-select-selection-search position:absolute
   * with left:11px (from the selector's inner border). Extra padding-left on the
   * selector does not move that layer, so the input/placeholder still drew under
   * the icon. Inset both the selector padding and the search box left offset.
   */
  &&.ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
    padding-left: ${p =>
      p.$hasIconLeft ? `${TEXT_INSET_LEFT_PX}px` : undefined};
  }

  &&.ant-select-single.ant-select-sm:not(.ant-select-customize-input)
    .ant-select-selector {
    padding-left: ${p =>
      p.$hasIconLeft ? `${TEXT_INSET_LEFT_PX}px` : undefined};
  }

  &&.ant-select-single.ant-select-lg:not(.ant-select-customize-input)
    .ant-select-selector {
    padding-left: ${p =>
      p.$hasIconLeft ? `${TEXT_INSET_LEFT_PX}px` : undefined};
  }

  &&.ant-select-single .ant-select-selector .ant-select-selection-search {
    left: ${p => (p.$hasIconLeft ? `${TEXT_INSET_LEFT_PX}px` : undefined)};
  }

  &&.ant-select-single.ant-select-sm:not(.ant-select-customize-input)
    .ant-select-selector
    .ant-select-selection-search {
    left: ${p => (p.$hasIconLeft ? `${TEXT_INSET_LEFT_PX}px` : undefined)};
  }
`;

const AutoComplete = React.forwardRef(
  ({ value, onChange, formatter, helpText, iconLeft, ...rest }, ref) => {
    const [displayAndRealValue, setDisplayAndRealValue] = useState(
      formatter ? formatter(value) : value
    );

    const formatedValue = formatter ? formatter(value) : value;
    const formattedDisplay =
      formatedValue?.display !== undefined
        ? formatedValue.display
        : formatedValue;

    useEffect(() => {
      setDisplayAndRealValue(formatedValue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formattedDisplay]);

    function changeValue(val) {
      let newFormattedValue = formatter ? formatter(val) : val;
      if (
        formatter &&
        typeof val === 'string' &&
        val.length > 0 &&
        newFormattedValue?.display === '' &&
        newFormattedValue?.value == null
      ) {
        // Keep user input visible while it is temporarily not parseable.
        newFormattedValue = { display: val, value: null };
      }

      setDisplayAndRealValue(newFormattedValue);

      if (newFormattedValue?.value !== undefined) {
        onChange(newFormattedValue.value);
      } else {
        onChange(newFormattedValue);
      }
    }

    const autoComplete = (
      <StyledAutoComplete
        {...rest}
        $hasIconLeft={!!iconLeft}
        value={
          displayAndRealValue?.display !== undefined
            ? displayAndRealValue.display
            : displayAndRealValue
        }
        onChange={changeValue}
        ref={ref}
      />
    );

    return (
      <div
        style={{ position: 'relative', width: '100%', minWidth: 0, flex: 1 }}
      >
        {helpText ? (
          <Tooltip title={helpText} placement='top'>
            {autoComplete}
          </Tooltip>
        ) : (
          autoComplete
        )}
        {iconLeft && <IconLeftContainer>{iconLeft}</IconLeftContainer>}
      </div>
    );
  }
);

export default AutoComplete;
